import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface PlotMapHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PlotMapHelpDialog: React.FC<PlotMapHelpDialogProps> = ({ open, onOpenChange }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Plot Map File Format</DialogTitle>
          <DialogDescription>
            How to format your CSV or Excel file for uploading plot data
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 text-sm pr-2">
          {/* Overview */}
          <section>
            <h3 className="font-semibold mb-2">Overview</h3>
            <p className="text-muted-foreground">
              A plot map file defines the individual plots in your experiment. Each row represents
              one plot, and columns contain plot identifiers, optional grid positions, and any
              metadata you want to track.
            </p>
          </section>

          {/* File Requirements */}
          <section>
            <h3 className="font-semibold mb-2">File Requirements</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Headers must be in the first row</li>
              <li>At least one data row is required</li>
              <li>One column must contain unique identifiers (Plot ID)</li>
              <li>Row and Column positions are optional (for grid layout)</li>
              <li>All other columns become searchable plot metadata</li>
              <li>Whitespace is automatically trimmed</li>
              <li>Data beyond the header columns will cause errors</li>
              <li>Maximum file size: 50MB for CSV, 10MB for Excel</li>
              <li>Supported formats: .csv, .xlsx, .xls, .xlsm</li>
            </ul>
          </section>

          {/* Example Table */}
          <section>
            <h3 className="font-semibold mb-2">Example Format</h3>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="bg-blue-50 font-semibold">plot_id</TableHead>
                    <TableHead className="bg-green-50">row</TableHead>
                    <TableHead className="bg-green-50">column</TableHead>
                    <TableHead>variety</TableHead>
                    <TableHead>treatment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="bg-blue-50/50">A-1-1</TableCell>
                    <TableCell className="bg-green-50/50">1</TableCell>
                    <TableCell className="bg-green-50/50">1</TableCell>
                    <TableCell>Albion</TableCell>
                    <TableCell>Control</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="bg-blue-50/50">A-1-2</TableCell>
                    <TableCell className="bg-green-50/50">1</TableCell>
                    <TableCell className="bg-green-50/50">2</TableCell>
                    <TableCell>Albion</TableCell>
                    <TableCell>Treated</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="bg-blue-50/50">A-2-1</TableCell>
                    <TableCell className="bg-green-50/50">2</TableCell>
                    <TableCell className="bg-green-50/50">1</TableCell>
                    <TableCell>Chandler</TableCell>
                    <TableCell>Control</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="bg-blue-50/50">A-2-2</TableCell>
                    <TableCell className="bg-green-50/50">2</TableCell>
                    <TableCell className="bg-green-50/50">2</TableCell>
                    <TableCell>Chandler</TableCell>
                    <TableCell>Treated</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <span className="inline-block w-3 h-3 bg-blue-100 border border-blue-200 rounded mr-1 align-middle"></span>
              Plot ID (required)
              <span className="inline-block w-3 h-3 bg-green-100 border border-green-200 rounded mx-1 ml-3 align-middle"></span>
              Row/Column (optional)
            </p>
          </section>

          {/* Column Types */}
          <section>
            <h3 className="font-semibold mb-2">Column Types</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-foreground">Plot ID (Required)</h4>
                <p className="text-muted-foreground">
                  A column with unique values that identifies each plot. Examples: "plot_id",
                  "plot_name", "id". This column will be used to reference plots during data collection.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-foreground">Row & Column (Optional)</h4>
                <p className="text-muted-foreground">
                  Integer columns that specify the grid position of each plot. When provided,
                  plots can be displayed in a visual grid layout. Each row/column combination
                  must be unique.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-foreground">Metadata (Optional)</h4>
                <p className="text-muted-foreground">
                  Any additional columns become searchable metadata attached to each plot.
                  Common examples: variety, treatment, replicate, block, location. This data
                  will be visible when collecting data and can be used to filter plots.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer with Close button */}
        <div className="flex-shrink-0 pt-4 border-t mt-4">
          <Button
            variant="default"
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlotMapHelpDialog;
